import React, { useCallback, useId } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  ARTIFACT_FORMATS,
  REQUIREMENT_DIMENSIONS,
} from '../utils/extractionTaxonomy';

/**
 * Form section for unified extraction settings: artifact format + dimension scope.
 *
 * @param {object} props
 * @param {{ artifactFormat: string, artifactFormatOther: string, excludedDimensions: string[] }} props.value
 * @param {function(object): void} props.onChange - Called with full next value when any field changes.
 * @returns {JSX.Element}
 */
function ExtractionConfigFormSection({ value, onChange }) {
  const { artifactFormat, artifactFormatOther, excludedDimensions } = value;
  const idBase = useId();

  const setField = useCallback(
    (patch) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange]
  );

  const toggleDimension = useCallback(
    (dimId, checked) => {
      const next = new Set(excludedDimensions || []);
      if (checked) {
        next.delete(dimId);
      } else {
        next.add(dimId);
      }
      setField({ excludedDimensions: [...next] });
    },
    [excludedDimensions, setField]
  );

  return (
    <Box
      component="fieldset"
      sx={{
        m: 0,
        p: { xs: 1.5, sm: 2 },
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-xl, 12px)',
        minWidth: 0,
      }}
    >
      <Typography component="legend" variant="subtitle2" sx={{ px: 0.5 }}>
        Extraction settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Choose how requirements are written and which dimensions the AI should consider.
      </Typography>
      <FormControl fullWidth sx={{ mb: 1.5 }}>
        <InputLabel id={`${idBase}-format-label`}>Artifact format</InputLabel>
        <Select
          labelId={`${idBase}-format-label`}
          label="Artifact format"
          value={artifactFormat}
          onChange={(e) => setField({ artifactFormat: e.target.value })}
          renderValue={(selectedId) => ARTIFACT_FORMATS.find((a) => a.id === selectedId)?.label || selectedId}
        >
          {ARTIFACT_FORMATS.map((a) => (
            <MenuItem key={a.id} value={a.id} sx={{ whiteSpace: 'normal' }}>
              <Box>
                <Typography variant="body2">{a.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.help}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {artifactFormat === 'Other' && (
        <TextField
          fullWidth
          label="Describe format"
          placeholder="e.g. Gherkin scenarios, OKRs"
          value={artifactFormatOther}
          onChange={(e) => setField({ artifactFormatOther: e.target.value })}
          sx={{ mb: 1.5 }}
        />
      )}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" id={`${idBase}-dimensions-label`}>
        Requirement dimensions
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Uncheck dimensions the AI should ignore. Each requirement can still carry several of the enabled tags.
      </Typography>
      <FormGroup
        aria-labelledby={`${idBase}-dimensions-label`}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 0.5 }}
      >
        {REQUIREMENT_DIMENSIONS.map((d) => (
          <FormControlLabel
            key={d.id}
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                size="small"
                checked={!excludedDimensions?.includes(d.id)}
                onChange={(e) => toggleDimension(d.id, e.target.checked)}
                sx={{ mt: -0.5 }}
              />
            }
            label={
              <Box sx={{ pb: 0.5 }}>
                <Typography variant="body2">{d.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {d.help}
                </Typography>
              </Box>
            }
          />
        ))}
      </FormGroup>
    </Box>
  );
}

export default ExtractionConfigFormSection;
